import { useContextDays } from '@rehookify/datepicker';
import styled from 'styled-components';

import { formatDate } from '../../../common/helpers/formatDate.helper';
import { IconCalendar } from '../../../common/icons/cdl/Calendar';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { Caption, CaptionProps } from '../../Caption/Caption';
import { InputLabel } from '../../Input/components/CoreInput';
import { InputContainer, InputContainerProps } from '../../Input/components/InputContainer';
import { InputLabelText } from '../../Input/components/InputLabelText';
import { LeadingVisualWrapper } from '../../Input/components/LeadingVisualWrapper';
import { Optional } from '../../Input/components/Optional';
import { Required } from '../../Input/components/Required';
import { getDateRangeInputBoxShadow, getDateRangeInputHoverBoxShadow } from '../helpers/getDateRangeInputStyles';

const DateRangeInputContainer = styled(InputContainer)<InputContainerProps>`
    cursor: pointer;
    box-shadow: ${(props) => getDateRangeInputBoxShadow(props)};

    &:hover {
        box-shadow: ${(props) => getDateRangeInputHoverBoxShadow(props)};
    }
`;

interface CustomDateRangeInputProps extends InputContainerProps {
    label?: string;
    markAsRequired?: boolean;
    markAsOptional?: boolean;
    caption?: string;
    CaptionIcon?: CaptionProps['Icon'];
}

export const CustomDateRangeInput = ({
    label,
    markAsRequired,
    markAsOptional,
    caption,
    CaptionIcon,
    ...rest
}: CustomDateRangeInputProps) => {
    const { selectedDates } = useContextDays();

    return (
        <InputLabel>
            <Box display="flex" flexDirection="column" gap={4}>
                {label ? (
                    <InputLabelText>
                        {label}
                        {markAsRequired ? <Required /> : null}
                        {markAsOptional ? <Optional /> : null}
                    </InputLabelText>
                ) : null}

                <DateRangeInputContainer {...rest}>
                    <LeadingVisualWrapper>
                        <IconCalendar width={16} height={16} color={theme.colors.foreground.muted} />
                    </LeadingVisualWrapper>
                    <Box padding="6px 10px" style={{ cursor: 'pointer' }}>
                        {selectedDates[0] ? formatDate({ date: selectedDates[0].getTime() }) + ' — ' : null}
                        {selectedDates[1] ? formatDate({ date: selectedDates[1].getTime() }) : null}
                    </Box>
                </DateRangeInputContainer>

                {caption ? (
                    <Caption $negative={rest.$negative} Icon={CaptionIcon}>
                        {caption}
                    </Caption>
                ) : null}
            </Box>
        </InputLabel>
    );
};
